import { Controller } from "@hotwired/stimulus"

const ORDER_SUBJECTS = [
  "order_where",
  "order_cancellation",
  "order_damaged",
  "order_return_and_refund"
]

export default class extends Controller {
  static targets = [
    "attachment",
    "extensionError",
    "fileInput",
    "fileList",
    "orderData",
    "orderDropdown",
    "orderDropdownButton",
    "orderInput",
    "orderOption",
    "orderSelection",
    "saleData",
    "saleInput",
    "sizeError",
    "subject"
  ]

  static values = {
    orderPrompt: String,
    submitted: Boolean
  }

  initialize() {
    this.closeOrderDropdownFromOutside = this.closeOrderDropdownFromOutside.bind(this)
    this.closeOrderDropdownFromEscape = this.closeOrderDropdownFromEscape.bind(this)
    this.selectedFiles = []
  }

  connect() {
    this.updateSubjectFields(false)
    this.restoreOrderSelection()
    document.addEventListener("click", this.closeOrderDropdownFromOutside)
    document.addEventListener("keydown", this.closeOrderDropdownFromEscape)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOrderDropdownFromOutside)
    document.removeEventListener("keydown", this.closeOrderDropdownFromEscape)
    if (this.fileErrorTimeout) clearTimeout(this.fileErrorTimeout)
  }

  validateFiles() {
    const allowedExtensions = ["jpg", "jpeg", "png", "zip", "pdf"]
    const maximumFileSize = Number.parseInt(this.fileInputTarget.dataset.maxFileSize, 10)
    const incomingFiles = Array.from(this.fileInputTarget.files)

    if (incomingFiles.length === 0) return

    const sizeExceeded = incomingFiles.some(file => maximumFileSize && file.size > maximumFileSize)
    const extensionError = incomingFiles.some(file => !allowedExtensions.includes(file.name.split(".").pop().toLowerCase()))

    this.hideFileErrors()

    if (sizeExceeded || extensionError) {
      // En cas d'erreur sur les nouveaux fichiers, on réinjecte les anciens valides
      this.syncFilesToInput()
      this.attachmentTarget.classList.add("error")
      this.sizeErrorTarget.hidden = !sizeExceeded
      this.extensionErrorTarget.hidden = !extensionError
      this.fileErrorTimeout = setTimeout(() => this.hideFileErrors(), 5000)
      return
    }

    // Ajout sans doublons (vérifie nom, taille et date de modification)
    incomingFiles.forEach(newFile => {
      const alreadyExists = this.selectedFiles.some(
        f => f.name === newFile.name && f.size === newFile.size && f.lastModified === newFile.lastModified
      )
      if (!alreadyExists) {
        this.selectedFiles.push(newFile)
      }
    })

    this.syncFilesToInput()
    this.renderFileList()
  }

  removeFile(event) {
    event.preventDefault()
    const indexToRemove = Number.parseInt(event.currentTarget.dataset.index, 10)

    this.selectedFiles.splice(indexToRemove, 1)
    this.syncFilesToInput()
    this.renderFileList()
  }

  syncFilesToInput() {
    const dt = new DataTransfer()
    this.selectedFiles.forEach(file => dt.items.add(file))
    this.fileInputTarget.files = dt.files
  }

  renderFileList() {
    if (!this.hasFileListTarget) return
    this.fileListTarget.innerHTML = ""

    this.selectedFiles.forEach((file, index) => {
      const li = document.createElement("li")
      li.className = "file-preview-item"

      const fileInfo = document.createElement("div")
      fileInfo.className = "file-info"

      const icon = document.createElement("span")
      icon.className = "file-badge-icon"
      icon.textContent = "📄"

      const name = document.createElement("span")
      name.className = "file-name"
      name.textContent = file.name

      const size = document.createElement("span")
      size.className = "file-size"
      const sizeInKb = file.size / 1024
      size.textContent = sizeInKb > 1024
        ? `${(sizeInKb / 1024).toFixed(1)} Mo`
        : `${Math.round(sizeInKb)} Ko`

      fileInfo.appendChild(icon)
      fileInfo.appendChild(name)
      fileInfo.appendChild(size)

      // Bouton supprimer (la petite croix)
      const removeBtn = document.createElement("button")
      removeBtn.type = "button" // Évite de soumettre le formulaire
      removeBtn.className = "file-remove-btn"
      removeBtn.setAttribute("data-action", "click->website--contact#removeFile")
      removeBtn.setAttribute("data-index", index)
      removeBtn.setAttribute("aria-label", "Supprimer le fichier")
      removeBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      `

      li.appendChild(fileInfo)
      li.appendChild(removeBtn)
      this.fileListTarget.appendChild(li)
    })
  }

  subjectChanged() {
    this.updateSubjectFields(true)
  }

  toggleOrderDropdown(event) {
    event.preventDefault()
    event.stopPropagation()
    const open = !this.orderDropdownTarget.classList.contains("open")
    this.setOrderDropdownOpen(open)
  }

  selectOrder(event) {
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return
    event.preventDefault()
    this.selectOrderOption(event.currentTarget)
    this.setOrderDropdownOpen(false)
  }

  updateSubjectFields(clearHiddenFields) {
    if (!this.hasSubjectTarget) return

    const displayOrder = ORDER_SUBJECTS.includes(this.subjectTarget.value)
    const displaySale = this.subjectTarget.value === "product_info"

    if (this.hasOrderDataTarget) this.orderDataTarget.hidden = !displayOrder
    if (this.hasSaleDataTarget) this.saleDataTarget.hidden = !displaySale

    if (clearHiddenFields && !displayOrder) this.clearOrder()
    if (clearHiddenFields && !displaySale && this.hasSaleInputTarget) this.saleInputTarget.value = ""
  }

  restoreOrderSelection() {
    if (!this.hasOrderInputTarget || !this.orderInputTarget.value || !this.hasOrderSelectionTarget) return

    const option = this.orderOptionTargets.find(orderOption => orderOption.dataset.id === this.orderInputTarget.value)
    if (option) this.selectOrderOption(option)
  }

  selectOrderOption(option) {
    this.orderInputTarget.value = option.dataset.id
    this.orderSelectionTarget.innerHTML = option.innerHTML
  }

  clearOrder() {
    if (this.hasOrderInputTarget) this.orderInputTarget.value = ""
    if (this.hasOrderSelectionTarget) this.orderSelectionTarget.textContent = this.orderPromptValue
  }

  setOrderDropdownOpen(open) {
    if (!this.hasOrderDropdownTarget) return

    this.orderDropdownTarget.classList.toggle("open", open)
    this.orderDropdownButtonTarget.setAttribute("aria-expanded", open.toString())
  }

  closeOrderDropdownFromOutside(event) {
    if (this.hasOrderDropdownTarget && !this.orderDropdownTarget.contains(event.target)) {
      this.setOrderDropdownOpen(false)
    }
  }

  closeOrderDropdownFromEscape(event) {
    if (event.key === "Escape") this.setOrderDropdownOpen(false)
  }

  hideFileErrors() {
    if (!this.hasAttachmentTarget) return

    if (this.fileErrorTimeout) clearTimeout(this.fileErrorTimeout)
    this.fileErrorTimeout = null
    this.attachmentTarget.classList.remove("error")
    this.sizeErrorTarget.hidden = true
    this.extensionErrorTarget.hidden = true
  }
}
